import { usersApi } from "#/api/usersApi";
import { type User, UserStatus } from "#/types/user";
import { logger } from "#/utils/logger";
import { Button, Modal, type ModalProps } from "@mantine/core";
import { IconUserCheck, IconUserOff } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export interface Props extends ModalProps {
  /** The user whose status is being flipped. The modal switches direction based on their current status. */
  user: User | undefined;
  onSuccess: () => void;
}

const UserStatusModal = ({ user, onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const isActive = user?.status === UserStatus.ACTIVE;
  const targetStatus = isActive ? UserStatus.DEACTIVATED : UserStatus.ACTIVE;

  const copy = isActive
    ? {
        title: t("clients.users.deactivateUser"),
        description: t("clients.users.deactivateDescription"),
        warning: t("clients.users.deactivateWarning"),
        confirmation: t("clients.users.deactivateConfirmation"),
        button: t("clients.users.deactivateButton"),
        color: "red",
        Icon: IconUserOff,
      }
    : {
        title: t("clients.users.activateUser"),
        description: t("clients.users.activateDescription"),
        warning: t("clients.users.activateWarning"),
        confirmation: t("clients.users.activateConfirmation"),
        button: t("clients.users.activateButton"),
        color: "teal",
        Icon: IconUserCheck,
      };

  const updateStatus = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("No user selected");
      return await usersApi.updateStatus(user.id, targetStatus);
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      logger.error("UserStatusModal updateStatus error:", error);
    },
  });

  return (
    <Modal
      {...props}
      centered
      title={copy.title}
      closeOnClickOutside={false}
      onClose={() => {
        props.onClose();
      }}
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col gap-2 text-sm">
          <span>{copy.description}</span>
          <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
            <span className="font-bold">{user?.name ?? "--"}</span>
            <span>{user?.email ?? "--"}</span>
          </div>
          <span>{copy.warning}</span>

          <span className="font-bold my-2">{copy.confirmation}</span>
        </div>

        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" onClick={() => props.onClose()}>
            {t("common.abort")}
          </Button>
          <Button
            onClick={() => updateStatus.mutate()}
            color={copy.color}
            size="sm"
            loading={updateStatus.isPending}
            disabled={!user}
            leftSection={<copy.Icon size={18} />}
          >
            {copy.button}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UserStatusModal;
